import React, { useState, useEffect } from 'react';
import { useBrandLogos, getPlatformLogoFromCache } from '../hooks/useBrandLogos';

interface BrandLogoProps {
  brandName?: string;
  platformName?: string;
  domain?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallbackText?: string;
  showFallback?: boolean;
}

const sizeClasses = {
  xs: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-12 h-12'
};

const fallbackSizeClasses = {
  xs: 'w-4 h-4 text-[8px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-12 h-12 text-base'
};

const BrandLogo: React.FC<BrandLogoProps> = ({
  brandName,
  platformName,
  domain,
  size = 'md',
  className = '',
  fallbackText,
  showFallback = true
}) => {
  const { fetchLogo, logos } = useBrandLogos();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadLogo = async () => {
      if (platformName) {
        // Handle platform logos with name normalization
        let normalizedPlatformName = platformName.toLowerCase();

        // Handle platform name variations
        if (normalizedPlatformName === 'judgeme') {
          normalizedPlatformName = 'judge.me';
        }

        const platformLogo = getPlatformLogoFromCache(normalizedPlatformName);
        if (platformLogo) {
          setLogoUrl(platformLogo);
          return;
        }
      }

      if (brandName) {
        // Handle brand logos
        const cacheKey = `${brandName.toLowerCase()}-${domain || 'no-domain'}`;
        const cachedLogo = logos.get(cacheKey);
        
        if (cachedLogo) {
          setLogoUrl(cachedLogo || null);
          return;
        }

        // Fetch logo if not in cache
        setIsLoading(true);
        try {
          const url = await fetchLogo(brandName, domain);
          setLogoUrl(url);
        } catch (error) {
          console.error('Error loading logo:', error);
          setHasError(true);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadLogo();
  }, [brandName, platformName, domain, fetchLogo, logos]);

  const handleImageError = () => {
    setHasError(true);
    setLogoUrl(null);
  };

  const getFallbackContent = () => {
    const text = fallbackText || brandName || platformName || '?';
    const initials = text
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);

    // Professional gradient backgrounds for different platforms
    const getGradientClass = () => {
      if (platformName) {
        const platform = platformName.toLowerCase();
        if (platform.includes('shopify')) return 'bg-gradient-to-br from-green-500 to-green-600';
        if (platform.includes('judge') || platform.includes('judgeme')) return 'bg-gradient-to-br from-orange-500 to-orange-600';
        if (platform.includes('yotpo')) return 'bg-gradient-to-br from-purple-500 to-purple-600';
        if (platform.includes('instagram')) return 'bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500';
        if (platform.includes('facebook')) return 'bg-gradient-to-br from-blue-600 to-blue-700';
      }

      // Brand-based gradient (hash the name for consistent colors)
      const hash = text.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);

      const colors = [
        'bg-gradient-to-br from-blue-500 to-blue-600',
        'bg-gradient-to-br from-purple-500 to-purple-600',
        'bg-gradient-to-br from-green-500 to-green-600',
        'bg-gradient-to-br from-red-500 to-red-600',
        'bg-gradient-to-br from-indigo-500 to-indigo-600',
        'bg-gradient-to-br from-pink-500 to-pink-600',
        'bg-gradient-to-br from-teal-500 to-teal-600',
        'bg-gradient-to-br from-orange-500 to-orange-600'
      ];

      return colors[Math.abs(hash) % colors.length];
    };

    return (
      <div className={`${fallbackSizeClasses[size]} ${className} ${getGradientClass()} rounded-lg flex items-center justify-center text-white font-semibold shadow-sm`}>
        {initials}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} ${className} bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse`} />
    );
  }

  if (!logoUrl || hasError) {
    return showFallback ? getFallbackContent() : null;
  }

  return (
    <img
      src={logoUrl}
      alt={`${brandName || platformName} logo`}
      className={`${sizeClasses[size]} ${className} object-contain rounded-lg`}
      onError={handleImageError}
      loading="lazy"
    />
  );
};

export default BrandLogo;
